/**
 * The steps of a Google Photos pick that need no window: the sign-in address
 * and its answer, the note one window leaves another, and the Picker API's
 * session — made, asked about until the gardener has chosen, read back.
 *
 * Kept apart from GooglePhotosService, which owns the windows and the tap, so
 * that these can be tested without a browser (googlePhotosFlow.test.mjs) and
 * shared with the page Google sends the window back to (googlePhotosReturn).
 * Nothing here imports the app's config or touches `window`: the fetch, the
 * storage, the clock and the waiting are all handed in.
 *
 * Why there is a second page at all: a browser lets one tap open one window.
 * Google's own sign-in popup (Identity Services) used that tap, so the picker
 * needed another — "tap twice". Now the tap opens one window that signs in
 * on Google's page, comes back to google-photos.html, makes the session
 * there and goes straight on to the picker. This tab learns the session from
 * the note left in localStorage.
 *
 * The pick handed over is also kept in localStorage (PendingPick) until its
 * photos are in. A phone puts Grow's tab away while the gardener is in Google
 * Photos (often in the Google Photos app), and Chrome may freeze it, throw
 * it away and reload it, or the gardener may come back by the window's Back
 * to Grow, which opens Grow afresh. In-memory waiting was lost every time,
 * and the photos chosen never arrived. Whichever Grow tab is in front picks
 * the record up again (claimPending makes sure only one does).
 *
 * Calls Google only (GOOGLE_OAUTH_AUTHORIZE_URL for the sign-in, with
 * GOOGLE_PICKER_SCOPE; GOOGLE_PICKER_API_URL for sessions and media items,
 * with the token as a Bearer header), never our backend.
 *
 * @module googlePhotosFlow
 */

// A relative path with its extension, so the tests run under Node's own
// TypeScript (node --experimental-strip-types), which knows no "@/".
import {
  GOOGLE_OAUTH_AUTHORIZE_URL,
  GOOGLE_PHOTOS_HANDOFF_KEY,
  GOOGLE_PHOTOS_PENDING_CLAIM_MS,
  GOOGLE_PHOTOS_PENDING_KEY,
  GOOGLE_PHOTOS_PENDING_MAX_MS,
  GOOGLE_PHOTOS_REQUEST_KEY,
  GOOGLE_PHOTOS_RETURN_PATH,
  GOOGLE_PHOTOS_SIGNIN_MAX_WAIT_MS,
  GOOGLE_PICKER_API_URL,
  GOOGLE_PICKER_DEFAULT_POLL_MS,
  GOOGLE_PICKER_MAX_WAIT_MS,
  GOOGLE_PICKER_NETWORK_RETRIES,
  GOOGLE_PICKER_PAGE_SIZE,
  GOOGLE_PICKER_SCOPE,
  GOOGLE_TOKEN_DEFAULT_LIFETIME_S,
  GOOGLE_TOKEN_EXPIRY_MARGIN_S,
} from '../../config/tunables/shared.ts';

/** Why a Google Photos attempt did not produce a photo. */
export type GooglePhotosFailure =
  | 'not-configured'
  | 'popup-blocked'
  | 'consent-denied'
  | 'scope-denied'
  | 'session-failed'
  | 'signed-out'
  | 'nothing-sent'
  | 'download-failed'
  | 'timed-out'
  | 'cancelled'
  | 'unknown';

/**
 * A Google Photos attempt that did not produce a photo, and why.
 *
 * The message is for whoever reads the logs, and may name Google's status or
 * the Cloud console. What the gardener is told is chosen by the reason
 * (useGooglePhotos), in their language.
 */
export class GooglePhotosError extends Error {
  public readonly reason: GooglePhotosFailure;

  /**
   * @param reason - Which way it failed; picks what the gardener is told.
   * @param message - What happened, for the logs.
   */
  constructor(reason: GooglePhotosFailure, message: string) {
    super(message);
    this.name = 'GooglePhotosError';
    this.reason = reason;
  }
}

/** One Picker API session, as Google describes it. */
export interface PickerSession {
  /** Google's id for the session. */
  id: string;
  /** Where the gardener picks (pickerAddress adds /autoclose). */
  pickerUri: string;
  /** True once the gardener has pressed Done. */
  mediaItemsSet?: boolean;
  /** Google's advice on how often to ask, and for how much longer ("5s", "1799s"). */
  pollingConfig?: { pollInterval?: string; timeoutIn?: string };
}

/** One picked photograph, as the Picker API describes it. */
export interface PickedMediaItem {
  /** Google's id for the item. */
  id: string;
  /**
   * When the photograph was taken, not when it reached Google Photos. The
   * bytes we fetch are a resize without the original's EXIF, so this is the
   * only place the real date survives.
   */
  createTime?: string;
  /** The file: its address (fetched through our server), name and type. */
  mediaFile?: {
    baseUrl?: string;
    filename?: string;
    mimeType?: string;
  };
}

/** A Google access token, and when to stop using it. */
export interface HeldToken {
  /** The access token itself; never logged. */
  value: string;
  /** Milliseconds since the epoch, a margin before Google's own expiry. */
  expiresAt: number;
}

/**
 * Where a pick's photos were going: the screen it was started from, and the
 * ids that screen needs. When that screen is no longer open by the time the
 * photos come (the dialog was closed, the tab reloaded), they are delivered
 * to what the screen was for (googlePhotosTarget); only a pick with no
 * target of its own goes to the Photos tray, on locationId's bed.
 */
export interface PickTarget {
  /**
   * The screen: a plant's photo, a bed's, the tray, a packet scan, Today's
   * picture, the profile photo, a photo being replaced, or another (which goes
   * to the tray).
   */
  kind: 'plant' | 'bed' | 'tray' | 'packet' | 'today' | 'profile' | 'replace' | 'other';
  /** The plant's plant_obj_id or the bed's location id, where the kind has one. */
  id?: number | null;
  /** The bed the photos belong to, for the tray, or the bed whose photo is replaced. */
  locationId?: number | null;
  /** For a replace: the plant whose photo is replaced (id is then the photo), or null for a bed's. */
  plantId?: number | null;
}

/** A sign-in under way in the Google Photos window, as this tab left it. */
export interface PickRequest {
  /** The random check Google hands back unchanged, so a stray answer is not taken. */
  state: string;
  /** Whether several photos may be picked. */
  many: boolean;
  /** The gardener's language, so the window speaks it too. */
  language: string;
  /** When the tap was, in milliseconds since the epoch. */
  createdAt: number;
  /** Where the photos are going, so the window can leave it in the PendingPick. */
  target?: PickTarget;
}

/**
 * A pick handed over and not yet brought in, kept in localStorage
 * (GOOGLE_PHOTOS_PENDING_KEY) so it outlives the tab that started it.
 */
export interface PendingPick {
  /** The pick's own id: the sign-in's state, or a fresh one when no sign-in was needed. */
  pickId: string;
  /** Google's id for the picking session. */
  sessionId: string;
  /** Google's advice on how often to ask ("5s"), when it gave one. */
  pollInterval?: string;
  /** The token the session was made with. */
  token: HeldToken;
  /** Whether every photo chosen is wanted, not only the first. */
  many: boolean;
  /** Where the photos were going. */
  target: PickTarget;
  /** When it was handed over, in milliseconds since the epoch. */
  createdAt: number;
  /** When to stop waiting: the session's or the token's end, whichever comes first. */
  expiresAt: number;
  /** The tab bringing it in, and when it last said so; null while no tab is. */
  claim?: { tab: string; at: number } | null;
}

/** What the Google Photos window hands back: a session to wait on, or why there is none. */
export type Handoff =
  | { state: string; ok: true; token: HeldToken; session: PickerSession; at: number }
  | { state: string; ok: false; reason: GooglePhotosFailure; detail: string; at: number };

/** What Google's answer to the sign-in said. */
export type AuthResult =
  | { ok: true; state: string; token: HeldToken }
  /** `state` is null when the answer was not for the sign-in this device started. */
  | { ok: false; state: string | null; reason: GooglePhotosFailure; detail: string };

/** The part of localStorage these steps use, so a test can hand in a Map. */
export type KeyValueStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** The part of fetch these steps use. */
export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

/** The type a window's postMessage carries a handoff under. */
export const HANDOFF_MESSAGE = 'grow-google-photos-handoff';

/**
 * Reads one of Google's durations ("5s", "1799.5s").
 *
 * @param value - Google's duration, or nothing.
 * @returns Milliseconds, or null when missing or unreadable. Zero is kept:
 *   a timeoutIn of "0s" means the session has run out.
 */
export function parseDuration(value: string | undefined): number | null {
  if (!value) return null;
  const seconds = Number.parseFloat(value.replace(/s$/, ''));
  return Number.isFinite(seconds) && seconds >= 0 ? seconds * 1000 : null;
}

/**
 * Turns Google's poll interval into milliseconds, with a fallback.
 *
 * @param value - Google's duration, or nothing.
 * @param fallback - Milliseconds to use when it is missing, unreadable or zero.
 */
export function durationToMs(value: string | undefined, fallback: number): number {
  const ms = parseDuration(value);
  return ms !== null && ms > 0 ? ms : fallback;
}

/**
 * A fresh random state for one sign-in.
 *
 * @param source - Where the randomness comes from (the browser's crypto).
 * @returns 32 hex characters.
 */
export function newState(source: Pick<Crypto, 'getRandomValues'> = globalThis.crypto): string {
  const bytes = source.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Where Google sends the window back, on this app's address.
 *
 * @param origin - The app's origin, e.g. https://grow.gardengameplan.com.
 */
export function returnUri(origin: string): string {
  return `${origin}${GOOGLE_PHOTOS_RETURN_PATH}`;
}

/**
 * The address of Google's sign-in for the picker's one scope.
 *
 * The token comes back in the address's fragment (response_type=token), the
 * flow Google documents for apps that run in the browser. No other scope is
 * asked for, and none granted before is folded in (no include_granted_scopes),
 * so the token can read the photos handed over and nothing else.
 *
 * @param options - The OAuth client, where to come back to, and the state.
 * @returns The address to open.
 */
export function buildAuthUrl(options: { clientId: string; redirectUri: string; state: string }): string {
  const params = new URLSearchParams({
    client_id: options.clientId,
    redirect_uri: options.redirectUri,
    response_type: 'token',
    scope: GOOGLE_PICKER_SCOPE,
    state: options.state,
  });
  return `${GOOGLE_OAUTH_AUTHORIZE_URL}?${params}`;
}

/**
 * Reads a JSON value from storage, quietly.
 *
 * @param store - Where to read.
 * @param key - Which key.
 * @returns The value, or null when missing, unreadable or storage is blocked.
 */
function readJson<T>(store: KeyValueStore, key: string): T | null {
  try {
    const raw = store.getItem(key);
    return raw ? JSON.parse(raw) as T : null;
  } catch {
    return null;
  }
}

/**
 * Removes a key, quietly.
 *
 * @param store - Where.
 * @param key - Which key.
 */
function removeQuietly(store: KeyValueStore, key: string): void {
  try { store.removeItem(key); } catch { /* blocked storage: nothing was kept */ }
}

/**
 * Leaves the sign-in under way for the window to find. Throws when storage is
 * blocked, since without it the window cannot hand anything back.
 *
 * @param store - localStorage, or a stand-in.
 * @param request - The sign-in.
 */
export function writeRequest(store: KeyValueStore, request: PickRequest): void {
  store.setItem(GOOGLE_PHOTOS_REQUEST_KEY, JSON.stringify(request));
}

/**
 * The sign-in under way, if there is one young enough to answer.
 *
 * @param store - localStorage, or a stand-in.
 * @param now - Milliseconds since the epoch.
 * @returns The request, or null when none or too old.
 */
export function readRequest(store: KeyValueStore, now: number): PickRequest | null {
  const request = readJson<PickRequest>(store, GOOGLE_PHOTOS_REQUEST_KEY);
  if (!request || typeof request.state !== 'string') return null;
  return now - Number(request.createdAt) <= GOOGLE_PHOTOS_SIGNIN_MAX_WAIT_MS ? request : null;
}

/**
 * Forgets the sign-in under way.
 *
 * @param store - localStorage, or a stand-in.
 */
export function clearRequest(store: KeyValueStore): void {
  removeQuietly(store, GOOGLE_PHOTOS_REQUEST_KEY);
}

/**
 * Reads Google's answer to the sign-in from the return address's fragment.
 *
 * The answer is taken only when its state is the one this device sent;
 * anything else could be someone else's token arriving on purpose.
 *
 * @param fragment - The return address's `#...`.
 * @param request - The sign-in this device started, if any.
 * @param now - Milliseconds since the epoch.
 * @returns The token, or why there is none.
 */
export function readAuthResponse(fragment: string, request: PickRequest | null, now: number): AuthResult {
  const params = new URLSearchParams(fragment.replace(/^#/, ''));
  const state = params.get('state');
  if (!request || !state || state !== request.state) {
    return { ok: false, state: null, reason: 'unknown', detail: 'The sign-in answer was not for a sign-in started here.' };
  }

  const error = params.get('error');
  if (error === 'access_denied') {
    return { ok: false, state, reason: 'consent-denied', detail: 'Access to Google Photos was declined.' };
  }
  if (error) {
    return { ok: false, state, reason: 'unknown', detail: `Google sign-in failed: ${error}` };
  }

  const value = params.get('access_token');
  if (!value) {
    return { ok: false, state, reason: 'unknown', detail: 'Google returned no access token.' };
  }

  // With granular consent the gardener can untick the one scope we asked for.
  const scopes = params.get('scope');
  if (scopes !== null && !scopes.split(/\s+/).includes(GOOGLE_PICKER_SCOPE)) {
    return { ok: false, state, reason: 'scope-denied', detail: 'Google Photos was left unticked on the consent screen.' };
  }

  const seconds = Number(params.get('expires_in')) || GOOGLE_TOKEN_DEFAULT_LIFETIME_S;
  return {
    ok: true,
    state,
    token: { value, expiresAt: now + (seconds - GOOGLE_TOKEN_EXPIRY_MARGIN_S) * 1000 },
  };
}

/**
 * Leaves the handoff for the app's tab. Quiet when storage is blocked: the
 * window also posts it to its opener where it can.
 *
 * @param store - localStorage, or a stand-in.
 * @param handoff - The session and token, or why there are none.
 */
export function writeHandoff(store: KeyValueStore, handoff: Handoff): void {
  try { store.setItem(GOOGLE_PHOTOS_HANDOFF_KEY, JSON.stringify(handoff)); } catch { /* see above */ }
}

/**
 * Reads whatever handoff is waiting, without taking it.
 *
 * @param store - localStorage, or a stand-in.
 * @returns The handoff, or null when none is waiting.
 */
export function readHandoff(store: KeyValueStore): Handoff | null {
  const handoff = readJson<Handoff>(store, GOOGLE_PHOTOS_HANDOFF_KEY);
  return handoff && typeof handoff.state === 'string' ? handoff : null;
}

/**
 * Takes the handoff for one sign-in, removing it so the token does not stay
 * on the device. A handoff for another state is left alone unless it is too
 * old to matter, when it is thrown away.
 *
 * @param store - localStorage, or a stand-in.
 * @param state - The sign-in's state.
 * @param now - Milliseconds since the epoch.
 * @returns The handoff, or null when it has not arrived.
 */
export function takeHandoff(store: KeyValueStore, state: string, now: number): Handoff | null {
  const handoff = readJson<Handoff>(store, GOOGLE_PHOTOS_HANDOFF_KEY);
  if (!handoff) return null;
  if (handoff.state === state) {
    removeQuietly(store, GOOGLE_PHOTOS_HANDOFF_KEY);
    return handoff;
  }
  if (now - Number(handoff.at) > GOOGLE_PHOTOS_SIGNIN_MAX_WAIT_MS) removeQuietly(store, GOOGLE_PHOTOS_HANDOFF_KEY);
  return null;
}

/**
 * The record of a pick just handed over.
 *
 * It lives as long as the session and the token both do: Google's timeoutIn
 * (capped at GOOGLE_PHOTOS_PENDING_MAX_MS, which also stands in when Google
 * says nothing), and never past the token's expiry.
 *
 * @param options - The pick's id, the session, its token, whether several
 *   photos are wanted, where they go, and the time now.
 * @returns The record, unclaimed.
 */
export function makePending(options: {
  pickId: string;
  session: PickerSession;
  token: HeldToken;
  many: boolean;
  target?: PickTarget;
  now: number;
}): PendingPick {
  const { pickId, session, token, many, now } = options;
  const googleSays = parseDuration(session.pollingConfig?.timeoutIn);
  const life = Math.min(googleSays ?? GOOGLE_PHOTOS_PENDING_MAX_MS, GOOGLE_PHOTOS_PENDING_MAX_MS);
  return {
    pickId,
    sessionId: session.id,
    pollInterval: session.pollingConfig?.pollInterval,
    token,
    many,
    target: options.target ?? { kind: 'other' },
    createdAt: now,
    expiresAt: Math.min(token.expiresAt, now + life),
    claim: null,
  };
}

/**
 * Keeps the pick handed over, replacing any other. Quiet when storage is
 * blocked: the tab that started it can still finish it from memory.
 *
 * @param store - localStorage, or a stand-in.
 * @param pending - The record.
 * @returns True when it was kept.
 */
export function writePending(store: KeyValueStore, pending: PendingPick): boolean {
  try {
    store.setItem(GOOGLE_PHOTOS_PENDING_KEY, JSON.stringify(pending));
    return true;
  } catch {
    return false;
  }
}

/**
 * The pick handed over and not yet brought in, expired or not.
 *
 * @param store - localStorage, or a stand-in.
 * @returns The record, or null when there is none or it is unreadable.
 */
export function readPending(store: KeyValueStore): PendingPick | null {
  const pending = readJson<PendingPick>(store, GOOGLE_PHOTOS_PENDING_KEY);
  if (!pending || typeof pending.pickId !== 'string' || typeof pending.sessionId !== 'string'
    || typeof pending.token?.value !== 'string' || !Number.isFinite(Number(pending.expiresAt))) {
    return null;
  }
  return { ...pending, target: pending.target ?? { kind: 'other' } };
}

/**
 * Whether a pending pick has run out: its session or its token is over, so
 * Google would no longer answer for it.
 *
 * @param pending - The record.
 * @param now - Milliseconds since the epoch.
 * @returns True once it has run out.
 */
export function isPendingExpired(pending: PendingPick, now: number): boolean {
  return now >= Number(pending.expiresAt);
}

/**
 * Forgets the pending pick.
 *
 * @param store - localStorage, or a stand-in.
 * @param pickId - Only this pick; a newer one kept since is left alone. Any
 *   pick when left out.
 */
export function clearPending(store: KeyValueStore, pickId?: string): void {
  if (pickId !== undefined && readPending(store)?.pickId !== pickId) return;
  removeQuietly(store, GOOGLE_PHOTOS_PENDING_KEY);
}

/** What claimPending found. */
export type ClaimResult = 'claimed' | 'held' | 'gone';

/**
 * Takes, or renews, one tab's hold on the pending pick, so two Grow tabs (the
 * one thrown away and reloaded, and the one Back to Grow opened) do not both
 * bring the photos in.
 *
 * A hold lasts GOOGLE_PHOTOS_PENDING_CLAIM_MS from its last renewal; one older
 * than that is from a tab that stopped, and is taken over.
 *
 * @param store - localStorage, or a stand-in.
 * @param pickId - The pick.
 * @param tab - This tab's id.
 * @param now - Milliseconds since the epoch.
 * @returns 'claimed' when this tab holds it now; 'held' when another tab
 *   holds it; 'gone' when the pick is no longer kept (brought in, cancelled,
 *   replaced, or never kept).
 */
export function claimPending(store: KeyValueStore, pickId: string, tab: string, now: number): ClaimResult {
  const pending = readPending(store);
  if (!pending || pending.pickId !== pickId) return 'gone';
  const claim = pending.claim;
  if (claim && claim.tab !== tab && now - Number(claim.at) < GOOGLE_PHOTOS_PENDING_CLAIM_MS) return 'held';
  if (!writePending(store, { ...pending, claim: { tab, at: now } })) return 'claimed';
  // Another tab may have written in between: whoever's hold is read back has it.
  const after = readPending(store);
  if (!after || after.pickId !== pickId) return 'gone';
  return after.claim?.tab === tab ? 'claimed' : 'held';
}

/**
 * Throws the right error for a refused Picker API request.
 *
 * @param response - Google's answer.
 * @param what - What was being asked, for the logs.
 */
function refused(response: Response, what: string): never {
  if (response.status === 401) {
    throw new GooglePhotosError('signed-out', `Google no longer accepts the sign-in (401) ${what}.`);
  }
  // 403 here is nearly always the project rather than the person: the Photos
  // Picker API not enabled, or the scope not on the consent screen.
  throw new GooglePhotosError(
    'session-failed',
    response.status === 403
      ? `Google refused ${what} (403). Check that the Photos Picker API is enabled for this project `
        + 'and that the picker scope is on the consent screen.'
      : `Google refused ${what} (${response.status}).`,
  );
}

/**
 * Asks Google for a picking session.
 *
 * @param fetchFn - fetch.
 * @param token - The access token.
 * @param many - Whether several photos may be picked; one otherwise, which
 *   Google's picker then enforces itself.
 * @param signal - Stops the request when the pick is cancelled.
 * @returns The session, with the address of its picker.
 */
export async function createSession(
  fetchFn: FetchLike,
  token: string,
  many: boolean,
  signal?: AbortSignal,
): Promise<PickerSession> {
  const response = await fetchFn(`${GOOGLE_PICKER_API_URL}/sessions`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(many ? {} : { pickingConfig: { maxItemCount: '1' } }),
    signal,
  });
  if (!response.ok) refused(response, 'a picking session');

  const session = await response.json() as PickerSession;
  if (!session?.pickerUri || !session?.id) {
    throw new GooglePhotosError('session-failed', 'Google did not return a picker to open.');
  }
  return session;
}

/**
 * The picker's address for a window this app opened: with /autoclose, Google
 * closes the window when the gardener taps Done, which on a phone puts them
 * straight back in Grow.
 *
 * @param session - The session.
 */
export function pickerAddress(session: PickerSession): string {
  return `${session.pickerUri.replace(/\/+$/, '')}/autoclose`;
}

/** What waiting for a choice needs handed in. */
export interface PollOptions {
  fetchFn: FetchLike;
  session: PickerSession;
  token: string;
  /** Waits between asks; may finish early (the gardener came back) or throw on cancel. */
  sleep: (ms: number, signal?: AbortSignal) => Promise<void>;
  /** Milliseconds since the epoch. */
  now: () => number;
  signal?: AbortSignal;
  /** The longest to wait at all; Google's own timeoutIn may cut it shorter. */
  maxWaitMs?: number;
  /** How many asks in a row may fail to reach Google before giving up. */
  retries?: number;
}

/**
 * Asks Google about the session until the gardener has chosen.
 *
 * Google says how often to ask, and how much longer the session lives, in
 * each answer's pollingConfig; both are honoured. Whether the picker's window
 * is still open is not asked: photos.google.com sends a Cross-Origin-Opener-
 * Policy that cuts this tab off from the window, which then reads as closed
 * while the gardener is still choosing. That was read as giving up, and the
 * pick ended with nothing. The gardener cancels with the dialog's Cancel.
 *
 * Once the wait is over (maxWaitMs, or Google's timeoutIn) Google is asked one
 * last time before giving up, so a tab that slept through the deadline still
 * finds photos chosen in the meantime.
 *
 * @param options - See PollOptions.
 * @returns When Google says the photos are chosen.
 * @throws GooglePhotosError: 'cancelled', 'timed-out', 'signed-out' or 'session-failed'.
 */
export async function pollUntilPicked(options: PollOptions): Promise<void> {
  const { fetchFn, session, token, sleep, now, signal } = options;
  const maxWaitMs = options.maxWaitMs ?? GOOGLE_PICKER_MAX_WAIT_MS;
  const retries = options.retries ?? GOOGLE_PICKER_NETWORK_RETRIES;
  const startedAt = now();
  /** When Google's session runs out, as Google last said. */
  let googleDeadline = startedAt + (parseDuration(session.pollingConfig?.timeoutIn) ?? maxWaitMs);
  let interval = durationToMs(session.pollingConfig?.pollInterval, GOOGLE_PICKER_DEFAULT_POLL_MS);
  let misses = 0;

  /** The error for a wait that ran out with nothing chosen. */
  const timedOut = () => new GooglePhotosError('timed-out', 'Google Photos was left open without a photo being chosen.');

  for (;;) {
    await sleep(interval, signal);
    if (signal?.aborted) throw new GooglePhotosError('cancelled', 'The pick was cancelled.');
    // Past the deadline Google is still asked once: a tab a phone froze while
    // the gardener chose wakes late, with the photos already chosen. It used
    // to give up before asking, and the photos never came.
    const late = now() >= Math.min(startedAt + maxWaitMs, googleDeadline);

    let response: Response;
    try {
      response = await fetchFn(`${GOOGLE_PICKER_API_URL}/sessions/${encodeURIComponent(session.id)}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      });
    } catch (error) {
      if (signal?.aborted) throw new GooglePhotosError('cancelled', 'The pick was cancelled.');
      if (late) throw timedOut();
      misses += 1;
      if (misses > retries) {
        throw new GooglePhotosError('session-failed', `Google could not be reached about the session: ${String(error)}`);
      }
      continue;
    }

    // Google having a bad moment is waited out like the network.
    if (response.status >= 500) {
      if (late) throw timedOut();
      misses += 1;
      if (misses > retries) refused(response, 'news of the session');
      continue;
    }
    if (!response.ok) refused(response, 'news of the session');
    misses = 0;

    const current = await response.json() as PickerSession;
    if (current.mediaItemsSet) return;
    if (late) throw timedOut();
    interval = durationToMs(current.pollingConfig?.pollInterval, interval);
    const left = parseDuration(current.pollingConfig?.timeoutIn);
    if (left !== null) googleDeadline = now() + left;
  }
}

/**
 * The photographs the gardener handed over, every page of them.
 *
 * @param fetchFn - fetch.
 * @param sessionId - The session they were picked in.
 * @param token - The token the session was made with.
 * @param signal - Stops the requests when the pick is cancelled.
 * @returns Google's description of each one.
 * @throws GooglePhotosError 'nothing-sent' when Google said photos were chosen but lists none.
 */
export async function listPicked(
  fetchFn: FetchLike,
  sessionId: string,
  token: string,
  signal?: AbortSignal,
): Promise<PickedMediaItem[]> {
  const items: PickedMediaItem[] = [];
  let pageToken = '';
  do {
    const query = new URLSearchParams({ sessionId, pageSize: String(GOOGLE_PICKER_PAGE_SIZE) });
    if (pageToken) query.set('pageToken', pageToken);
    const response = await fetchFn(`${GOOGLE_PICKER_API_URL}/mediaItems?${query}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    });
    if (!response.ok) refused(response, 'the list of chosen photos');
    const body = await response.json() as { mediaItems?: PickedMediaItem[]; nextPageToken?: string };
    items.push(...(body.mediaItems ?? []));
    pageToken = body.nextPageToken ?? '';
  } while (pageToken);

  if (items.length === 0) {
    throw new GooglePhotosError('nothing-sent', 'Google said photos were chosen but did not list any.');
  }
  return items;
}

/**
 * Closes a session once it has served; a failure is not worth raising.
 *
 * @param fetchFn - fetch.
 * @param sessionId - The session.
 * @param token - The token it was made with.
 */
export async function deleteSession(fetchFn: FetchLike, sessionId: string, token: string): Promise<void> {
  try {
    await fetchFn(`${GOOGLE_PICKER_API_URL}/sessions/${encodeURIComponent(sessionId)}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch { /* it expires on its own */ }
}

/**
 * The day Google says a photo was taken.
 *
 * @param item - The picked photo.
 * @returns The date, or null when Google did not say or said nonsense.
 */
export function takenAtOf(item: PickedMediaItem): Date | null {
  const stamped = item.createTime ? new Date(item.createTime) : null;
  return stamped && !Number.isNaN(stamped.getTime()) ? stamped : null;
}
